import api from "./axios";

export interface AcademicYear {
    id: string;
    name: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
    semesters_count?: number;
    created_at?: string;
    updated_at?: string;
}

export interface AcademicYearFormData {
    name: string;
    start_date: string;
    end_date: string;
    is_active?: boolean;
}

export interface AcademicYearListResponse {
    success: boolean;
    message: string;
    data: {
        items: AcademicYear[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    };
}

export interface AcademicYearResponse {
    success: boolean;
    message: string;
    data: AcademicYear;
}

const academicYearApi = {
    // Get all academic years with pagination
    getAcademicYears: async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
        is_active?: boolean;
    }): Promise<{
        success: boolean;
        message: string;
        data: { items: AcademicYear[]; meta: any };
    }> => {
        // Custom params serializer to ensure boolean false is included
        const paramsSerializer = (params: any) => {
            const searchParams = new URLSearchParams();
            Object.keys(params).forEach((key) => {
                const value = params[key];
                if (value !== undefined && value !== null) {
                    searchParams.append(key, value.toString());
                }
            });
            return searchParams.toString();
        };

        const response = await api.get("/v1/academic-years", {
            params,
            paramsSerializer,
        });
        return response.data;
    },

    // Get academic year by ID
    getAcademicYear: async (id: string): Promise<AcademicYearResponse> => {
        const response = await api.get(`/v1/academic-years/${id}`);
        return response.data;
    },

    // Create new academic year
    createAcademicYear: async (
        data: AcademicYearFormData,
    ): Promise<AcademicYearResponse> => {
        const response = await api.post("/v1/academic-years", data);
        return response.data;
    },

    // Update academic year
    updateAcademicYear: async (
        id: string,
        data: AcademicYearFormData,
    ): Promise<AcademicYearResponse> => {
        const response = await api.put(`/v1/academic-years/${id}`, data);
        return response.data;
    },

    // Delete academic year
    deleteAcademicYear: async (id: string): Promise<{ success: boolean }> => {
        const response = await api.delete(`/v1/academic-years/${id}`);
        return response.data;
    },

    // Set academic year as active
    setActiveAcademicYear: async (
        id: string,
    ): Promise<AcademicYearResponse> => {
        const response = await api.post(`/v1/academic-years/${id}/set-active`);
        return response.data;
    },

    // Get active academic year
    getActiveAcademicYear: async (): Promise<AcademicYearResponse> => {
        const response = await api.get("/v1/academic-years/active");
        return response.data;
    },
};

export default academicYearApi;
